import { useState, useEffect, useCallback } from "react";
import {
  getTagsApi,
  getTagDetailApi,
  associateTagApi,
  deleteTagApi,
  TAG_ENTITY,
} from "../api/tag.api";
import usePermissions from "./usePermissions";
import ErrorModal from "../components/ErrorModal";

const entityKey = {
  [TAG_ENTITY.COMPANY]: "companies",
  [TAG_ENTITY.CONTACT]: "contacts",
  [TAG_ENTITY.PITCH]: "pitches",
  [TAG_ENTITY.PITCH_SCORE]: "pitchScores",
};

const useTags = ({ entity, currentPage = null }) => {
  const [selectedTag, setSelectedTag] = useState("");
  const [tags, setTags] = useState([]);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalResults: 0,
    totalPages: 0,
    pageSize: 50,
  });
  const [loading, setLoading] = useState(false);

  const permissions = usePermissions();

  const fetchTags = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getTagsApi();
      setTags(response.data || []);
    } catch (error) {
      console.error("Error fetching tags:", error);
      ErrorModal.show(error);
      setTags([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTagDetail = useCallback(
    async (pageNum = null) => {
      // Use external currentPage if provided, otherwise use internal pagination
      const targetPage =
        pageNum !== null
          ? pageNum
          : currentPage !== null
          ? currentPage
          : pagination.currentPage;

      try {
        setLoading(true);
        const response = await getTagDetailApi({
          id: selectedTag,
          entity,
          pageNum: targetPage,
        });

        setData(response.data[entityKey[entity]] || []);

        // Update pagination info
        const entityData = response;
        setPagination({
          currentPage: entityData.pageNum || targetPage || 1,
          totalResults: entityData.totalResults || 0,
          totalPages: entityData.totalPages || 0,
          pageSize: entityData.pageSize || 50,
        });
      } catch (error) {
        console.error("Error fetching tag detail:", error);
        ErrorModal.show(error);
        setData([]);
        setPagination({
          currentPage: targetPage || 1,
          totalResults: 0,
          totalPages: 0,
          pageSize: 50,
        });
      } finally {
        setLoading(false);
      }
    },
    [selectedTag, entity, currentPage, pagination.currentPage],
  );

  useEffect(() => {
    if (permissions.favourites) {
      fetchTags();
    }
  }, [permissions.favourites]);

  const reloadTags = async () => {
    if (permissions.favourites) {
      await fetchTags();
    }
  };

  const reloadTagDetail = async () => {
    if (selectedTag && entity && permissions.favourites) {
      await fetchTagDetail();
    }
  };

  useEffect(() => {
    if (selectedTag && entity && permissions.favourites) {
      const initialPage = currentPage !== null ? currentPage : 1;
      fetchTagDetail(initialPage);
    } else {
      setData([]);
      setPagination({
        currentPage: currentPage !== null ? currentPage : 1,
        totalResults: 0,
        totalPages: 0,
        pageSize: 50,
      });
    }
  }, [selectedTag, entity, permissions.favourites, currentPage]);

  // Function to change page
  const setPage = async (pageNum) => {
    if (selectedTag && entity && permissions.favourites) {
      await fetchTagDetail(pageNum);
    }
  };

  const addToTag = async (entityIds) => {
    try {
      setLoading(true);

      if (!selectedTag) {
        throw new Error("No tag selected");
      }

      // Prepare data based on entity type
      const data = {};

      // Set the appropriate key based on entity
      if (entity === TAG_ENTITY.COMPANY) {
        data.companies = entityIds;
      } else if (entity === TAG_ENTITY.CONTACT) {
        data.contacts = entityIds;
      } else if (entity === TAG_ENTITY.PITCH) {
        data.pitches = entityIds;
      } else if (entity === TAG_ENTITY.PITCH_SCORE) {
        data.pitchScores = entityIds;
      }

      await associateTagApi({ id: selectedTag, data });

      // Refresh the tag detail data
      await fetchTagDetail();
    } catch (error) {
      console.error("Error adding to tag:", error);
      ErrorModal.show(error);
    } finally {
      setLoading(false);
    }
  };

  const deleteTag = async (tagId) => {
    try {
      setLoading(true);
      await deleteTagApi({ id: tagId });

      // Reload the tags list after successful deletion
      await fetchTags();

      // Clear selected tag if it was the one deleted
      if (selectedTag === tagId) {
        setSelectedTag("");
        setData([]);
      }
    } catch (error) {
      console.error("Error deleting tag:", error);
      ErrorModal.show(error);
    } finally {
      setLoading(false);
    }
  };

  return {
    selectedTag,
    setSelectedTag,
    tags,
    data,
    pagination,
    loading,
    addToTag,
    deleteTag,
    reloadTags,
    setPage,
    reloadTagDetail,
  };
};

export default useTags;
